import { AutoComplete } from 'antd';
import FormItem from 'antd/es/form/FormItem';
import { useLineBotJoinLogs } from '../utils/api.hook';
import { map, some } from 'lodash';
import moment from 'moment';
import { useMemo } from 'react';

/**
 * LINE Group ID 的格式：C 開頭 + 32 碼小寫十六進位。
 * 後端 create-provider.handler.ts 用同一份規則，兩邊要一起改。
 */
export const LINE_GROUP_ID_PATTERN = /^C[0-9a-f]{32}$/;

/**
 * 供應商的 LINE Group ID 欄位。
 *
 * 原本是唯讀的 <Select>，只能從 bot 的加群紀錄挑。但供應商 LINE OA 的 webhook 已改
 * 指向新系統的 bot，這份紀錄不會再自己長出新的群組，只能選就等於綁不了新供應商。
 * 改用 AutoComplete：保留既有紀錄當建議清單，同時允許直接貼上 group id。
 */
export function LineGroupIdInputComponent() {
  const { data: { logs } = {}, isLoading } = useLineBotJoinLogs();

  const logOptions = useMemo(
    () =>
      map(logs, (log: { id: string; time: string }) => ({
        label: `${moment(log.time).utcOffset(8).format('YYYY-MM-DD HH:mm')} - ${
          log.id
        }`,
        value: log.id,
      })),
    [logs]
  );

  return (
    <FormItem
      noStyle
      shouldUpdate={(prev, next) => prev.lineGroupId !== next.lineGroupId}
    >
      {({ getFieldValue }) => {
        const value: string | undefined = getFieldValue('lineGroupId');
        const isUnknown =
          !!value && !some(logOptions, (option) => option.value === value);

        return (
          <FormItem
            name="lineGroupId"
            label="LINE Group ID"
            extra={
              isUnknown
                ? '此 ID 不在 bot 加群紀錄中（新 bot 已接手 webhook，手動填入屬正常情況）'
                : undefined
            }
            rules={[
              {
                required: true,
                message: '請輸入LINE Group ID',
              },
              {
                pattern: LINE_GROUP_ID_PATTERN,
                message: 'LINE Group ID 格式不正確（C 開頭共 33 碼）',
              },
            ]}
          >
            <AutoComplete
              allowClear
              options={logOptions}
              disabled={isLoading}
              placeholder="可直接貼上 LINE Group ID，或從加群紀錄挑選"
              filterOption={(input, option) =>
                String(option?.value ?? '')
                  .toLowerCase()
                  .includes(input.toLowerCase())
              }
            />
          </FormItem>
        );
      }}
    </FormItem>
  );
}
